import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, LocalizedDateInput, SearchableSelect, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useAssetsModuleVisible } from '../../assets/assetsModule';
import { getAssets } from '../../assets/services/assetService';
import { Asset } from '../../assets/types/Asset';
import { getContacts } from '../../contacts/services/contactService';
import { Contact } from '../../contacts/types/Contact';
import { getProducts } from '../../products/services/productService';
import { Product } from '../../products/types/Product';
import { getServices } from '../../services/services/serviceCatalogService';
import { ServiceOffering } from '../../services/types/Service';
import {
  createEmptyLine,
  formatMoney,
  invoiceToDraftLines,
  linesToRequest,
  pickDefaultLocationId,
  previewInvoiceTotals,
  toDateInput,
  toIsoDate,
  applyContactToBillTo,
  billToUpdatePayload,
  emptyBillTo,
  formatPartyLines,
  invoiceToBillTo,
  joinBillToEmails,
  sellerFromInvoice,
  validBillToEmails,
} from '../invoiceForm';
import { exportInvoiceToPdf, buildInvoicePdfBlob, InvoicePdfLabels } from '../invoicePdf';
import {
  createCreditNote,
  createPayment,
  deleteInvoice,
  deletePayment,
  getInvoiceById,
  issueInvoice,
  sendInvoiceEmail,
  updateInvoice,
  voidInvoice,
} from '../services/invoiceService';
import { CreateInvoicePaymentRequest, Invoice } from '../types/Invoice';
import { InvoiceAddPaymentModal } from './InvoiceAddPaymentModal';
import { InvoiceBillToEmails, InvoiceBillToFields } from './InvoiceBillToFields';
import { InvoiceLinesEditor } from './InvoiceLinesEditor';
import catalog from '../../catalog/Catalog.module.css';
import { listLocations } from '../../companies';
import { Location } from '../../companies/types/Company';
import styles from '../Invoice.module.css';

const statusLabelKey = (status: string) => `status${status.charAt(0).toUpperCase()}${status.slice(1)}`;
type InvoiceDetailsTab = 'invoice' | 'payments';

export const InvoiceDetails: React.FC = () => {
  const { t } = useTranslation(['invoices', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const [item, setItem] = React.useState<Invoice | null>(null);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [assets, setAssets] = React.useState<Asset[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [services, setServices] = React.useState<ServiceOffering[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [contactId, setContactId] = React.useState('');
  const [assetId, setAssetId] = React.useState('');
  const [locationId, setLocationId] = React.useState('');
  const [locations, setLocations] = React.useState<Location[]>([]);
  const [dueDate, setDueDate] = React.useState('');
  const [customerNote, setCustomerNote] = React.useState('');
  const [internalNote, setInternalNote] = React.useState('');
  const [pricesIncludeTax, setPricesIncludeTax] = React.useState(false);
  const [lines, setLines] = React.useState([createEmptyLine()]);
  const [billTo, setBillTo] = React.useState(emptyBillTo());
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const [emailBusy, setEmailBusy] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<InvoiceDetailsTab>('invoice');
  const canUpdate = hasPermission(PermissionScopes.invoicesUpdate);
  const canCreate = hasPermission(PermissionScopes.invoicesCreate);
  const canDelete = hasPermission(PermissionScopes.invoicesDelete);

  const applyInvoice = React.useCallback((invoice: Invoice) => {
    setItem(invoice);
    setContactId(invoice.contactId);
    setAssetId(invoice.assetId || '');
    setLocationId(invoice.locationId || '');
    setDueDate(toDateInput(invoice.dueDate));
    setCustomerNote(invoice.customerNote || '');
    setInternalNote(invoice.internalNote || '');
    setPricesIncludeTax(invoice.pricesIncludeTax);
    setLines(invoiceToDraftLines(invoice));
    setBillTo(invoiceToBillTo(invoice));
  }, []);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [invoice, contactData, productData, serviceData, assetData, locationData] = await Promise.all([
          id?.trim() ? getInvoiceById(id) : Promise.resolve(null),
          getContacts({ pageSize: 200 }),
          getProducts({ page: 1, pageSize: 200 }),
          getServices({ page: 1, pageSize: 200 }),
          isAssetsModuleVisible ? getAssets({ page: 1, pageSize: 200 }) : Promise.resolve([]),
          listLocations({ isActive: true, pageSize: 200 }).catch(() => [] as Location[]),
        ]);
        setContacts(contactData);
        setProducts(productData);
        setServices(serviceData);
        setAssets(assetData);
        setLocations(locationData);
        if (invoice) {
          applyInvoice(invoice);
          if (invoice.status === 'draft' && !invoice.locationId) {
            setLocationId(pickDefaultLocationId(locationData));
          }
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [applyInvoice, id, isAssetsModuleVisible]);

  const run = async (work: () => Promise<Invoice | null>, fallbackKey: string) => {
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await work();
      if (updated) applyInvoice(updated);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t(fallbackKey, { ns: 'invoices' }));
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!id || !item) return;
    if (item.status === 'draft') {
      await run(() => updateInvoice(id, {
        contactId,
        assetId: assetId || undefined,
        clearAsset: !assetId,
        locationId: locationId || undefined,
        clearLocation: !locationId,
        pricesIncludeTax,
        dueDate: toIsoDate(dueDate),
        clearDueDate: !dueDate,
        ...billToUpdatePayload(billTo),
        customerNote: customerNote.trim() || undefined,
        clearCustomerNote: !customerNote.trim(),
        internalNote: internalNote.trim() || undefined,
        clearInternalNote: !internalNote.trim(),
        lines: linesToRequest(lines).filter(line => line.quantity > 0),
      }), 'failedToSave');
      return;
    }

    await run(() => updateInvoice(id, {
      dueDate: toIsoDate(dueDate),
      clearDueDate: !dueDate,
      billToEmail: joinBillToEmails(billTo.emails) || undefined,
      clearBillToEmail: joinBillToEmails(billTo.emails).length === 0,
      customerNote: customerNote.trim() || undefined,
      clearCustomerNote: !customerNote.trim(),
      internalNote: internalNote.trim() || undefined,
      clearInternalNote: !internalNote.trim(),
    }), 'failedToSave');
  };

  const onIssue = async () => {
    if (!id || !window.confirm(t('confirmIssue', { ns: 'invoices' }))) return;
    await run(() => issueInvoice(id), 'failedToIssue');
  };

  const onVoid = async () => {
    if (!id || !window.confirm(t('confirmVoid', { ns: 'invoices' }))) return;
    await run(() => voidInvoice(id), 'failedToVoid');
  };

  const onDelete = async () => {
    if (!id || !window.confirm(t('confirmDelete', { ns: 'invoices' }))) return;
    setSaving(true);
    try {
      await deleteInvoice(id);
      navigate('/invoices');
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToDelete', { ns: 'invoices' }));
      setSaving(false);
    }
  };

  const onCredit = async () => {
    if (!id || !window.confirm(t('confirmCredit', { ns: 'invoices' }))) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const credit = await createCreditNote(id);
      if (credit) navigate(`/invoices/${credit.id}`);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToCredit', { ns: 'invoices' }));
    } finally {
      setSaving(false);
    }
  };

  const onAddPayment = async (request: CreateInvoicePaymentRequest) => {
    if (!id) return t('failedToPay', { ns: 'invoices' });
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await createPayment(id, request);
      if (updated) applyInvoice(updated);
      setPaymentOpen(false);
      setActiveTab('payments');
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : t('failedToPay', { ns: 'invoices' });
    } finally {
      setSaving(false);
    }
  };

  const pdfLabels = React.useCallback((): InvoicePdfLabels => ({
    invoice: t('typeInvoice', { ns: 'invoices' }),
    creditNote: t('typeCreditNote', { ns: 'invoices' }),
    from: t('seller', { ns: 'invoices' }),
    billTo: t('billTo', { ns: 'invoices' }),
    taxId: t('taxId', { ns: 'invoices' }),
    issueDate: t('issueDate', { ns: 'invoices' }),
    dueDate: t('dueDate', { ns: 'invoices' }),
    relatedInvoice: t('relatedInvoice', { ns: 'invoices' }),
    description: t('lineName', { ns: 'invoices' }),
    quantity: t('quantity', { ns: 'invoices' }),
    unitPrice: t('unitPrice', { ns: 'invoices' }),
    taxPercent: t('taxPercent', { ns: 'invoices' }),
    lineTotal: t('lineTotal', { ns: 'invoices' }),
    subtotal: t('subtotal', { ns: 'invoices' }),
    taxTotal: t('taxTotal', { ns: 'invoices' }),
    total: t('total', { ns: 'invoices' }),
    paid: t('paidTotal', { ns: 'invoices' }),
    remaining: t('remaining', { ns: 'invoices' }),
    overpaid: t('overpaid', { ns: 'invoices' }),
    customerNote: t('customerNote', { ns: 'invoices' }),
    page: t('page', { ns: 'invoices' }),
    of: t('of', { ns: 'invoices' }),
  }), [t]);

  const onDownloadPdf = async () => {
    if (!item) return;
    const snapshot = {
      ...item,
      billToEmail: joinBillToEmails(billTo.emails) || undefined,
      ...(item.status === 'draft'
        ? {
            billToName: billTo.name.trim() || item.billToName,
            billToPhone: billTo.phone.trim() || undefined,
            billToAddressLine1: billTo.addressLine1.trim() || undefined,
            billToAddressLine2: billTo.addressLine2.trim() || undefined,
            billToCity: billTo.city.trim() || undefined,
            billToStateOrProvince: billTo.stateOrProvince.trim() || undefined,
            billToPostalCode: billTo.postalCode.trim() || undefined,
            billToCountry: billTo.country.trim() || undefined,
            customerNote: customerNote.trim() || undefined,
            dueDate: toIsoDate(dueDate) || undefined,
          }
        : {}),
    };
    setPdfBusy(true);
    setGeneralError(null);
    try {
      await exportInvoiceToPdf(snapshot, pdfLabels(), t(statusLabelKey(item.status), { ns: 'invoices' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToPdf', { ns: 'invoices' }));
    } finally {
      setPdfBusy(false);
    }
  };

  const onSendEmail = async () => {
    if (!id || !item) return;
    const recipients = validBillToEmails(billTo.emails);
    if (recipients.length === 0) {
      setGeneralError(t('emailToRequired', { ns: 'invoices' }));
      return;
    }
    if (!window.confirm(t('confirmSendEmail', { ns: 'invoices', count: recipients.length }))) return;

    const joined = joinBillToEmails(recipients);
    setEmailBusy(true);
    setGeneralError(null);
    try {
      if (canUpdate && item.status !== 'void') {
        const saved = await updateInvoice(id, {
          billToEmail: joined,
          clearBillToEmail: false,
        });
        if (saved) applyInvoice(saved);
      }

      const { blob, fileName } = await buildInvoicePdfBlob(
        { ...item, billToEmail: joined },
        pdfLabels(),
        t(statusLabelKey(item.status), { ns: 'invoices' }),
      );
      await sendInvoiceEmail(item.id, { to: joined, pdf: blob, fileName });
      setEmailSent(joined);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToEmail', { ns: 'invoices' }));
    } finally {
      setEmailBusy(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingItem', { ns: 'invoices' })} /></div>;
  }

  if (!item) {
    return <div className="error">{t('notFound', { ns: 'invoices' })}</div>;
  }

  const isDraft = item.status === 'draft';
  const isSales = item.documentType === 'invoice';
  const canPay = canUpdate && isSales && (item.status === 'issued' || item.status === 'partiallyPaid') && item.remaining > 0;
  const canVoid = canUpdate && item.status !== 'draft' && item.status !== 'void'
    && (item.documentType === 'creditNote' ? item.status !== 'paid' : item.payments.length === 0);
  const canEmail = canUpdate && item.status !== 'draft' && item.status !== 'void';
  const title = item.number || t('statusDraft', { ns: 'invoices' });
  const sellerLines = formatPartyLines(sellerFromInvoice(item, t('taxId', { ns: 'invoices' })));
  const billToLines = formatPartyLines({ ...billTo, email: undefined });
  const emailButton = canEmail ? (
    <button
      className={styles.iconButton}
      type="button"
      disabled={saving || emailBusy}
      aria-label={t('emailInvoice', { ns: 'invoices' })}
      title={t('emailInvoice', { ns: 'invoices' })}
      onClick={() => void onSendEmail()}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 7.2C4 6.08 4.9 5.2 6 5.2h12c1.1 0 2 .88 2 1.98v9.6c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V7.2Z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <path d="m5 7 7 5 7-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  ) : null;
  const draftTotals = previewInvoiceTotals(lines, pricesIncludeTax);
  const summary = isDraft
    ? { ...draftTotals, paidTotal: item.paidTotal, remaining: draftTotals.total, overpaid: 0 }
    : {
      subtotal: item.subtotal,
      taxTotal: item.taxTotal,
      total: item.total,
      paidTotal: item.paidTotal,
      remaining: item.remaining,
      overpaid: item.overpaid,
    };

  return (
    <div className={`${catalog.page} ${styles.page}`}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'invoices' }), path: '/invoices' },
        { label: title, path: `/invoices/${item.id}` },
      ]} />
      <div className={catalog.headerRow}>
        <div>
          <h2 className={catalog.title}>{title}</h2>
          <span className={`${catalog.statusBadge} ${item.status === 'draft' ? styles.statusDraft : item.status === 'void' ? styles.statusVoid : item.status === 'partiallyPaid' ? styles.statusPartial : styles.statusIssued}`}>
            {t(statusLabelKey(item.status), { ns: 'invoices' })}
          </span>
        </div>
        <div className={catalog.headerActions}>
          <button className="btn btn-secondary" type="button" disabled={saving || pdfBusy} onClick={() => void onDownloadPdf()}>
            {t('downloadPdf', { ns: 'invoices' })}
          </button>
          {isDraft && canUpdate ? <button className="btn btn-primary" type="button" disabled={saving} onClick={onIssue}>{t('issue', { ns: 'invoices' })}</button> : null}
          {isSales && !isDraft && item.status !== 'void' && canCreate ? <button className="btn btn-secondary" type="button" disabled={saving} onClick={onCredit}>{t('createCreditNote', { ns: 'invoices' })}</button> : null}
          {canVoid ? <button className="btn btn-secondary" type="button" disabled={saving} onClick={onVoid}>{t('void', { ns: 'invoices' })}</button> : null}
          {isDraft && canDelete ? <button className="btn btn-secondary" type="button" disabled={saving} onClick={onDelete}>{t('deleteDraft', { ns: 'invoices' })}</button> : null}
        </div>
      </div>
      <p className={styles.hint}>{item.documentType === 'creditNote' ? t('creditHint', { ns: 'invoices' }) : isDraft ? t('draftHint', { ns: 'invoices' }) : t('issuedHint', { ns: 'invoices' })}</p>
      {generalError ? <div className="error">{generalError}</div> : null}
      {emailSent ? <p className={styles.hint}>{t('emailSent', { ns: 'invoices', email: emailSent })}</p> : null}

      <div className={styles.tabs} role="tablist" aria-label={t('detailsTitle', { ns: 'invoices' })}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'invoice'}
          className={activeTab === 'invoice' ? styles.tabButtonActive : styles.tabButton}
          onClick={() => setActiveTab('invoice')}
        >
          {t('typeInvoice', { ns: 'invoices' })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'payments'}
          className={activeTab === 'payments' ? styles.tabButtonActive : styles.tabButton}
          onClick={() => setActiveTab('payments')}
        >
          {t('payments', { ns: 'invoices' })}
        </button>
      </div>

      <div className={styles.workspace}>
        <div className={styles.mainColumn}>
          {activeTab === 'invoice' ? (
            <>
          <div className={styles.metaBand}>
            <div>
              <p className={catalog.detailLabel}>{t('documentType', { ns: 'invoices' })}</p>
              <p className={catalog.detailValue}>{item.documentType === 'creditNote' ? t('typeCreditNote', { ns: 'invoices' }) : t('typeInvoice', { ns: 'invoices' })}</p>
            </div>
            <div>
              <p className={catalog.detailLabel}>{t('customer', { ns: 'invoices' })}</p>
              {isDraft && canUpdate ? (
                <SearchableSelect
                  value={contactId}
                  onChange={nextContactId => {
                    setContactId(nextContactId);
                    const contact = contacts.find(entry => entry.id === nextContactId);
                    if (contact) setBillTo(previous => applyContactToBillTo(previous, contact));
                  }}
                  options={contacts.map(contact => ({ value: contact.id, label: contact.name, meta: contact.email }))}
                  inputClassName={catalog.input}
                />
              ) : <p className={catalog.detailValue}>{item.contactName}</p>}
            </div>
            {item.relatedInvoiceId ? (
              <div>
                <p className={catalog.detailLabel}>{t('relatedInvoice', { ns: 'invoices' })}</p>
                <p className={catalog.detailValue}>
                  <button className="btn btn-secondary" type="button" onClick={() => navigate(`/invoices/${item.relatedInvoiceId}`)}>
                    {item.relatedInvoiceNumber || item.relatedInvoiceId}
                  </button>
                </p>
              </div>
            ) : null}
            {isAssetsModuleVisible ? (
              <div>
                <p className={catalog.detailLabel}>{t('asset', { ns: 'invoices' })}</p>
                {isDraft && canUpdate ? (
                  <SearchableSelect
                    value={assetId}
                    onChange={setAssetId}
                    options={assets.map(asset => ({ value: asset.id, label: asset.name }))}
                    clearOption={{ value: '', label: t('noAsset', { ns: 'invoices' }) }}
                    inputClassName={catalog.input}
                  />
                ) : <p className={catalog.detailValue}>{item.assetName || '-'}</p>}
              </div>
            ) : null}
            {locations.length > 0 || item.locationName ? (
              <div>
                <p className={catalog.detailLabel}>{t('location', { ns: 'invoices' })}</p>
                {isDraft && canUpdate && locations.length > 0 ? (
                  <select className={catalog.input} value={locationId} onChange={event => setLocationId(event.target.value)}>
                    <option value="">{t('noLocation', { ns: 'invoices' })}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>{location.name}</option>
                    ))}
                    {item.locationId && !locations.some(location => location.id === item.locationId) ? (
                      <option value={item.locationId}>{item.locationName || item.locationId}</option>
                    ) : null}
                  </select>
                ) : <p className={catalog.detailValue}>{item.locationName || t('noLocation', { ns: 'invoices' })}</p>}
              </div>
            ) : null}
            <div>
              <p className={catalog.detailLabel}>{t('dueDate', { ns: 'invoices' })}</p>
              {canUpdate && item.status !== 'void' ? (
                <LocalizedDateInput value={dueDate} onValueChange={setDueDate} emptyLabel={t('dueDate', { ns: 'invoices' })} />
              ) : <p className={catalog.detailValue}>{toDateInput(item.dueDate) || '-'}</p>}
            </div>
          </div>

          <div className={catalog.headerRow}>
            <h3 className={styles.sectionTitle}>{t('lines', { ns: 'invoices' })}</h3>
            {isDraft && canUpdate ? (
              <button className="btn btn-secondary" type="button" onClick={() => setLines(prev => [...prev, createEmptyLine()])}>
                {t('addLine', { ns: 'invoices' })}
              </button>
            ) : null}
          </div>
          {isDraft && canUpdate ? (
            <InvoiceLinesEditor lines={lines} pricesIncludeTax={pricesIncludeTax} products={products} services={services} onChange={setLines} />
          ) : (
            <div className={styles.lineList}>
              <div className={styles.issuedLineHead}>
                <span>{t('lineName', { ns: 'invoices' })}</span>
                <span>{t('quantity', { ns: 'invoices' })}</span>
                <span className={styles.issuedLineAmount}>{t('unitPrice', { ns: 'invoices' })}</span>
                <span className={styles.issuedLineAmount}>{t('taxPercent', { ns: 'invoices' })}</span>
                <span className={styles.issuedLineAmount}>{t('lineTotal', { ns: 'invoices' })}</span>
              </div>
              {item.lines.map(line => (
                <div key={line.id} className={styles.issuedLineRow}>
                  <div>
                    <span className={catalog.secondaryText}>{t(`line${line.lineType.charAt(0).toUpperCase()}${line.lineType.slice(1)}`, { ns: 'invoices' })}</span>
                    <div className={catalog.primaryText}>{line.name}</div>
                  </div>
                  <div>{line.quantity}</div>
                  <div className={styles.issuedLineAmount}>{formatMoney(line.unitPrice, item.currency)}</div>
                  <div className={styles.issuedLineAmount}>{line.taxPercent}%</div>
                  <div className={styles.issuedLineAmount}>{formatMoney(line.lineTotal, item.currency)}</div>
                </div>
              ))}
            </div>
          )}

          <div className={styles.partyGrid}>
            <div className={styles.partyCard}>
              <p className={catalog.detailLabel}>{t('seller', { ns: 'invoices' })}</p>
              {sellerLines.length > 0 ? (
                <ul className={styles.partyLines}>
                  {sellerLines.map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : <p className={catalog.detailValue}>{t('sellerAfterIssue', { ns: 'invoices' })}</p>}
            </div>
            <div className={styles.partyCard}>
              <p className={catalog.detailLabel}>{t('billTo', { ns: 'invoices' })}</p>
              {isDraft && canUpdate ? (
                <>
                  <p className={styles.hint}>{t('billToHint', { ns: 'invoices' })}</p>
                  <InvoiceBillToFields value={billTo} onChange={setBillTo} sendAction={emailButton} />
                </>
              ) : (
                <>
                  {billToLines.length > 0 ? (
                    <ul className={styles.partyLines}>
                      {billToLines.map(line => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  ) : <p className={catalog.detailValue}>-</p>}
                  {canUpdate && item.status !== 'void' ? (
                    <InvoiceBillToEmails
                      emails={billTo.emails}
                      sendAction={emailButton}
                      onChange={emails => setBillTo(previous => ({ ...previous, emails }))}
                    />
                  ) : (
                    <p className={catalog.detailValue}>{item.billToEmail || '-'}</p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={catalog.detailItemWide}>
            <p className={catalog.detailLabel}>{t('customerNote', { ns: 'invoices' })}</p>
            {canUpdate && item.status !== 'void'
              ? <textarea className={catalog.textarea} value={customerNote} onChange={event => setCustomerNote(event.target.value)} />
              : <p className={catalog.detailValue}>{item.customerNote || '-'}</p>}
          </div>
          <div className={catalog.detailItemWide}>
            <p className={catalog.detailLabel}>{t('internalNote', { ns: 'invoices' })}</p>
            {canUpdate && item.status !== 'void'
              ? <textarea className={catalog.textarea} value={internalNote} onChange={event => setInternalNote(event.target.value)} />
              : <p className={catalog.detailValue}>{item.internalNote || '-'}</p>}
          </div>
            </>
          ) : (
            <>
              {(isDraft || canPay) ? (
                <div className={catalog.headerRow}>
                  {isDraft ? <p className={styles.hint}>{t('paymentsAfterIssue', { ns: 'invoices' })}</p> : <span />}
                  {canPay ? (
                    <button className="btn btn-primary" type="button" disabled={saving} onClick={() => setPaymentOpen(true)}>
                      {t('addPayment', { ns: 'invoices' })}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {item.payments.length === 0 ? <div className={catalog.emptyState}>{t('noPayments', { ns: 'invoices' })}</div> : (
                <div className={styles.payments}>
                  {item.payments.map(payment => (
                    <div key={payment.id} className={styles.paymentRow}>
                      <div>
                        <div>{t(`method${payment.method.charAt(0).toUpperCase()}${payment.method.slice(1)}`, { ns: 'invoices' })}</div>
                        {payment.note ? <div className={catalog.secondaryText}>{payment.note}</div> : null}
                      </div>
                      <span>{formatMoney(payment.amount, item.currency)}</span>
                      <span>{toDateInput(payment.paidAt)}</span>
                      {canUpdate && payment.method !== 'creditNote' && item.status !== 'void' ? (
                        <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => run(() => deletePayment(item.id, payment.id), 'failedToPay')}>
                          {t('deletePayment', { ns: 'invoices' })}
                        </button>
                      ) : <span />}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <aside className={styles.summaryAside}>
          <h3 className={styles.summaryTitle}>{t('summary', { ns: 'invoices' })}</h3>
          <dl>
            <div className={styles.summaryRow}>
              <dt>{t('subtotal', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(summary.subtotal, item.currency)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{t('taxTotal', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(summary.taxTotal, item.currency)}</dd>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryRowStrong}`}>
              <dt>{t('total', { ns: 'invoices' })}</dt>
              <dd>{formatMoney(summary.total, item.currency)}</dd>
            </div>
            {!isDraft ? (
              <>
                <div className={styles.summaryRow}>
                  <dt>{t('paymentsAndCredits', { ns: 'invoices' })}</dt>
                  <dd>{formatMoney(summary.paidTotal, item.currency)}</dd>
                </div>
                <div className={`${styles.summaryRow} ${styles.summaryRowStrong}`}>
                  <dt>{t(summary.overpaid > 0 ? 'overpaid' : 'remaining', { ns: 'invoices' })}</dt>
                  <dd>{formatMoney(summary.overpaid > 0 ? summary.overpaid : summary.remaining, item.currency)}</dd>
                </div>
              </>
            ) : null}
          </dl>
          {(canPay || (canUpdate && item.status !== 'void' && activeTab === 'invoice')) ? (
            <div className={styles.summaryActions}>
              {canPay ? (
                <button className="btn btn-primary" type="button" disabled={saving} onClick={() => setPaymentOpen(true)}>
                  {t('addPayment', { ns: 'invoices' })}
                </button>
              ) : null}
              {canUpdate && item.status !== 'void' && activeTab === 'invoice' ? (
                <button className={canPay ? 'btn btn-secondary' : 'btn btn-primary'} type="button" disabled={saving} onClick={onSave}>
                  {t('save', { ns: 'invoices' })}
                </button>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
      {paymentOpen && canPay ? (
        <InvoiceAddPaymentModal
          remaining={item.remaining}
          currency={item.currency}
          busy={saving}
          onClose={() => setPaymentOpen(false)}
          onSubmit={onAddPayment}
        />
      ) : null}
    </div>
  );
};

export default InvoiceDetails;
